import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	linkedSignal,
	OnDestroy,
	Signal,
	signal,
	viewChild
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import {
	PRISM_DIALOG_DATA,
	PrismButtonComponent,
	PrismCardComponent,
	PrismDialogRef,
	PrismIconComponent,
	PrismStepComponent,
	PrismStepper,
	PrismStepperComponent
} from '@shure/prism-angular-components';
import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DoNotShowAgainCheckboxComponent } from '@shure/shared/angular/ui/components/dialogs/common';
import { NewDraggableDialogComponent } from '@shure/shared/angular/ui/components/draggable-dialog';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

import { OnboardingStage } from './models';
import { Coverage3dOnboardingDeviceBehaviorService } from './services';
import { Coverage3DOnboardingService } from './services/coverage-3-d-onboarding.service';
import {
	OnboardingStageTitlePipe,
	OnboardingStageDescriptionPipe,
	OnboardingStepTitlePipe,
	OnboardingStepDescriptionPipe,
	OnboardingActionDescriptionPipe
} from './ui';

export interface OnboardingCoverage3DDialogData {
	deviceModel: Signal<DeviceModel>;
	doNotShowDialogId?: string;
}

@Component({
	selector: 'sh-onboarding-coverage-3-d',
	imports: [
		NewDraggableDialogComponent,
		DoNotShowAgainCheckboxComponent,
		PrismButtonComponent,
		PrismCardComponent,
		PrismIconComponent,
		PrismStepperComponent,
		PrismStepComponent,
		TranslocoModule,
		OnboardingStageTitlePipe,
		OnboardingStageDescriptionPipe,
		OnboardingStepTitlePipe,
		OnboardingStepDescriptionPipe,
		OnboardingActionDescriptionPipe
	],
	providers: [provideInlineScope('scCoverage3dOnboarding', (lang) => import(`../i18n/${lang}`))],
	templateUrl: './onboarding-coverage-3-d.component.html',
	styleUrl: './onboarding-coverage-3-d.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnboardingCoverage3DComponent implements OnDestroy {
	private readonly dialogRef = inject(PrismDialogRef<OnboardingCoverage3DComponent>);
	protected readonly onboardingService = inject(Coverage3DOnboardingService);
	protected readonly data = inject<OnboardingCoverage3DDialogData>(PRISM_DIALOG_DATA);

	protected readonly behaviorService = inject(Coverage3dOnboardingDeviceBehaviorService);

	public static readonly id = 'sh-onboarding-coverage-3d';

	private readonly stepper = viewChild<PrismStepper>('stepper');

	public readonly onboardingSteps = computed(() => {
		return this.behaviorService.getOnboardingStepsConfigs(this.data.deviceModel());
	});

	public readonly activeStepIndex = signal<number>(-1);

	private readonly activeStep = computed(() => {
		const index = this.activeStepIndex();
		return index >= 0 ? this.onboardingSteps().at(index) : undefined;
	});

	protected readonly activeStepActions = linkedSignal({
		source: this.activeStep,
		computation: (step) => step?.actions.map((action) => ({ action, completed: false })) ?? []
	});

	protected readonly activeStepActionsCompleted = computed(() => this.activeStepActions().every((a) => a.completed));

	protected readonly onboardingStageEnum = OnboardingStage;
	protected readonly onboardingStage = computed(() => {
		const index = this.activeStepIndex();
		if (index < 0) {
			return OnboardingStage.Introduction;
		} else if (index < this.onboardingSteps().length) {
			return OnboardingStage.InProgress;
		}
		return OnboardingStage.Completed;
	});

	protected doNotShowAgain = signal<boolean>(false);

	constructor() {
		effect(() => {
			const activeStep = this.activeStep();
			this.onboardingService.activeStep.set(activeStep?.name);
			this.onboardingService.setOnboardingDevice(activeStep?.device);
			this.onboardingService.onboardingAction.set(undefined);
		});

		effect(() => {
			const action = this.onboardingService.onboardingAction();
			this.activeStepActions.update((actions) =>
				actions.map((a) => ({ ...a, completed: a.completed || a.action === action }))
			);
		});
	}

	public ngOnDestroy(): void {
		this.onboardingService.resetOnboardingState();
	}

	public onCloseDialog(): void {
		this.dialogRef.close({ doNotShowDialog: this.doNotShowAgain() });
	}

	protected onIncrementStep(): void {
		const index = this.activeStepIndex();
		const stepper = this.stepper();
		if (stepper && index < this.onboardingSteps().length - 1) {
			stepper.next();
		} else {
			this.activeStepIndex.update((index) => index + 1);
			this.onboardingService.setOnboardingDevice(undefined);
		}
	}

	protected onDecrementStep(): void {
		const index = this.activeStepIndex();
		const stepper = this.stepper();
		if (stepper && index > 0) {
			stepper.previous();
		} else {
			this.activeStepIndex.set(-1);
			this.onboardingService.setOnboardingDevice(undefined);
		}
	}

	protected onActiveStepChange($event: CustomEvent<number>): void {
		this.activeStepIndex.set($event.detail);
	}
}
